import { useFpB2bCartCalculate } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-calculate'
import { useFpB2bCouponService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-coupon-service'
import { useFpB2bCoupons } from '@cart-services/hooks/fp-b2b/use-fp-b2b-coupons'
import { useFpB2bMerchantService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-merchant-service'
import { useCouponManualInput } from '@cart-services/hooks/useCouponManualInput'
import { type GetCouponManualInputExceptionMessageReturn } from '@cart-services/utils/errorHandlers'
import { CouponManualInput } from '@cart-ui/CouponManualInput/CouponManualInput'
import { type CountryCode, type ProjectCode } from '@constants'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiCalculateCampaignCoupons } from '@hooks-api/fortune/useApiCalculateCampaignCoupons'
import { useApiCampaignGetCouponIdByCode } from '@hooks-api/fortune/useApiCampaignGetCouponIdByCode'
import { UsageMethod } from '@hooks-api/types/dataModel/coupon'
import { type InputProps } from '@pets-ui/Input/Input'
import { type SnackbarVariantsProps } from '@pets-ui/Snackbar/SnackbarVariants'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useCart } from '../CartProvider/CartProvider'
export interface DialogCouponManualInputProps {
  campaignId: number
  projectCode: ProjectCode
  countryCode: CountryCode
  campaignCode?: string
  onApplySuccess?: (coupon_id: number) => void
}

export const DialogCouponManualInput = (props: DialogCouponManualInputProps) => {
  const { campaignCode, campaignId, projectCode, countryCode, onApplySuccess } = props
  const { platform } = useCart()
  const { merchant } = useFpB2bMerchantService(campaignId, platform)
  const { calculateValue } = useFpB2bCartCalculate(campaignId, campaignCode, platform)
  const { addManualInputCouponIds, removeManualInputCouponIds } = useFpB2bCouponService(
    campaignId,
    projectCode,
    countryCode,
    campaignCode,
    platform,
  )
  const { trigger: getCouponIdByCodeTrigger, isMutating: isGetCouponIdByCodeMutating } = useApiCampaignGetCouponIdByCode()
  const { trigger: calculateCouponsTrigger, isMutating: isCalculateCampaignCouponsMutating } = useApiCalculateCampaignCoupons({
    campaign_code: campaignCode,
  })
  const { openSnackbar } = useSnackbar<SnackbarVariantsProps>()
  const { availableMergedCoupons } = useFpB2bCoupons(campaignId, projectCode, countryCode, campaignCode, platform)
  const { t } = useTranslation()

  const getCouponIdByCode = useCallback((couponCode: string) => getCouponIdByCodeTrigger({
    project_code: projectCode,
    campaign_code: campaignCode,
    coupon_code: couponCode,
    merchant_id: merchant.index,
  }), [campaignCode, getCouponIdByCodeTrigger, merchant.index, projectCode])

  const calculateCoupons = useCallback((manualInputCouponIds: number[]) => calculateCouponsTrigger({
    ...calculateValue,
    manual_input_coupon_ids: manualInputCouponIds,
  }), [calculateValue, calculateCouponsTrigger])

  const onApplyCoupon = useCallback((manualInputCouponIds: number[]) => {
    addManualInputCouponIds(manualInputCouponIds)
  }, [addManualInputCouponIds])

  const onApplyCouponSuccess = useCallback((message: string, manualInputCouponIds: number[]) => {
    openSnackbar({
      message,
      severity: 'success',
    })
    if (manualInputCouponIds[0]) {
      onApplySuccess?.(manualInputCouponIds[0])
    }
  }, [openSnackbar, onApplySuccess])

  const onApplyCouponSuccessNotQualified = useCallback((message: string, manualInputCouponIds: number[]) => {
    openSnackbar({
      message,
      severity: 'warning',
    })
    if (manualInputCouponIds[0]) {
      onApplySuccess?.(manualInputCouponIds[0])
    }
  }, [openSnackbar, onApplySuccess])

  const onApplyCouponError = useCallback((message: string, manualInputCouponIds: number[]) => {
    removeManualInputCouponIds(manualInputCouponIds)
    openSnackbar({
      message,
      severity: 'error',
    })
  }, [openSnackbar, removeManualInputCouponIds])

  const onGetCouponError = useCallback((exceptionMessage: GetCouponManualInputExceptionMessageReturn) => {
    openSnackbar({
      message: t(exceptionMessage.message, { ...exceptionMessage.value, defaultValue: exceptionMessage.message }),
      severity: 'error',
    })
  }, [openSnackbar, t])

  /**
   * 不直接從 calculateValue.manual_input_coupon_ids 取得，
   * 因為 calculateValue.manual_input_coupon_ids 可能包含已過期的優惠券
   */
  const currentManualInputCouponIds = useMemo(() =>
    availableMergedCoupons?.filter(coupon => coupon.usage_method === UsageMethod.MANUAL).map(coupon => coupon.coupon_id),
  [availableMergedCoupons])

  const {
    couponCode,
    setCouponCode,
    handleSubmit,
  } = useCouponManualInput({
    getCouponIdByCode,
    calculateCoupons,
    onApplyCoupon,
    onApplyCouponSuccess,
    onApplyCouponSuccessNotQualified,
    onApplyCouponError,
    onGetCouponError,
    currentManualInputCouponIds,
  })

  const handleChange: InputProps['onChange'] = useCallbackEvent((event) => {
    setCouponCode(event.target.value)
  })

  return (
    <CouponManualInput
      onSubmit={handleSubmit}
      value={couponCode}
      onChange={handleChange}
      disabled={isGetCouponIdByCodeMutating || isCalculateCampaignCouponsMutating}
      placeholder="請輸入優惠碼"
      submitText="抵用"
    />
  )
}
